import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, RefreshCw, Plus, Archive, CalendarClock, ShieldCheck } from 'lucide-react';
import { API, CHANGE_FIELDS, fmtDate } from '../../lib/planConstants';

export default function ChangeSyncModal({ plan, horizonOptions, authHeader, onClose, onApplied }) {
  const [rows, setRows] = useState([{ field: 'career_goal', from: '', to: '' }]);
  const [newEndpoint, setNewEndpoint] = useState({ endpoint_type: '', target_date: '', custom_label: '' });
  const [diff, setDiff] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [apply, setApply] = useState({ dates: true, archive: true, additions: true });

  const preview = async () => {
    const changes = rows.filter((r) => r.to.trim());
    if (!changes.length && !newEndpoint.endpoint_type) {
      return toast.error('Add at least one change.');
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/futurepath-plan/change-preview`, {
        changes,
        new_endpoint: newEndpoint.endpoint_type ? newEndpoint : null,
      }, authHeader);
      setDiff(r.data.diff);
      setPreviewId(r.data.preview_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not analyse the impact.');
    } finally {
      setBusy(false);
    }
  };

  const resync = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/futurepath-plan/resync`, {
        preview_id: previewId, apply_dates: apply.dates,
        apply_archive: apply.archive, apply_additions: apply.additions,
      }, authHeader);
      toast.success(`Plan updated, ${r.data.tasks_added} added, ${r.data.tasks_archived} archived, ${r.data.dates_changed} re-dated.`);
      onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update the plan.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0a1729]/70 overflow-y-auto p-4" onClick={onClose} data-testid="change-sync-modal">
      <div className="max-w-3xl mx-auto my-8 rounded-2xl border border-cyan-500/20 bg-[#08203A] p-6"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-semibold">Something changed</p>
            <h2 className="text-lg md:text-lg font-semibold text-white mt-1">Update my plan</h2>
            <p className="text-sm text-slate-300 mt-1">
              Tell us what changed. We'll show the impact before touching anything, completed work is always kept.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-300 hover:text-white" data-testid="change-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!diff && (
          <div className="mt-5 space-y-3">
            {rows.map((row, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <select value={row.field} onChange={(e) => setRows(rows.map((r, j) => j === i ? { ...r, field: e.target.value } : r))}
                  className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid={`change-field-${i}`}>
                  {CHANGE_FIELDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input value={row.from} onChange={(e) => setRows(rows.map((r, j) => j === i ? { ...r, from: e.target.value } : r))}
                  placeholder="Was…" className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid={`change-from-${i}`} />
                <input value={row.to} onChange={(e) => setRows(rows.map((r, j) => j === i ? { ...r, to: e.target.value } : r))}
                  placeholder="Now…" className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid={`change-to-${i}`} />
              </div>
            ))}
            <button onClick={() => setRows([...rows, { field: 'gpa', from: '', to: '' }])}
              className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1.5" data-testid="add-change-row">
              <Plus className="w-3.5 h-3.5" /> Add another change
            </button>

            <div className="rounded-xl border border-slate-700/80 bg-slate-900/40 p-4 mt-2">
              <p className="text-[11px] uppercase tracking-wider text-slate-300 mb-2">Moving your endpoint? (optional)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <select value={newEndpoint.endpoint_type}
                  onChange={(e) => setNewEndpoint({ ...newEndpoint, endpoint_type: e.target.value })}
                  className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="change-endpoint-type">
                  <option value="">Keep {plan.horizon?.label} ({fmtDate(plan.horizon?.target_date)})</option>
                  {(horizonOptions || []).filter((o) => o.group !== 'ai').map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
                <input type="date" value={newEndpoint.target_date}
                  onChange={(e) => setNewEndpoint({ ...newEndpoint, target_date: e.target.value })}
                  className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="change-endpoint-date" />
              </div>
            </div>

            <button onClick={preview} disabled={busy}
              className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold"
              data-testid="preview-change-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Show me the impact
            </button>
          </div>
        )}

        {diff && (
          <div className="mt-5 space-y-4" data-testid="change-diff">
            <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4">
              <p className="text-sm text-white font-medium">{diff.summary}</p>
              <p className="text-sm text-slate-300 mt-1">{diff.why}</p>
            </div>

            {(diff.affected_milestones || []).length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-300 mb-1.5">Milestones affected</p>
                <ul className="space-y-1">
                  {diff.affected_milestones.map((m, i) => (
                    <li key={i} className="text-sm text-slate-300">{m.title}, <span className="text-slate-300">{m.impact}</span></li>
                  ))}
                </ul>
              </div>
            )}

            {(diff.tasks_to_add || []).length > 0 && (
              <label className="block rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4">
                <span className="text-sm font-semibold text-emerald-300 flex items-center gap-2">
                  <input type="checkbox" checked={apply.additions} onChange={(e) => setApply({ ...apply, additions: e.target.checked })}
                    data-testid="apply-additions" />
                  <Plus className="w-4 h-4" /> {diff.tasks_to_add.length} task(s) to add
                </span>
                <ul className="mt-2 space-y-1">
                  {diff.tasks_to_add.map((t, i) => (
                    <li key={i} className="text-sm text-slate-300">{t.title} <span className="text-xs text-slate-300">· due {fmtDate(t.due_date)}</span></li>
                  ))}
                </ul>
              </label>
            )}

            {(diff.tasks_to_archive || []).length > 0 && (
              <label className="block rounded-xl border border-amber-500/25 bg-amber-500/5 p-4">
                <span className="text-sm font-semibold text-amber-300 flex items-center gap-2">
                  <input type="checkbox" checked={apply.archive} onChange={(e) => setApply({ ...apply, archive: e.target.checked })}
                    data-testid="apply-archive" />
                  <Archive className="w-4 h-4" /> {diff.tasks_to_archive.length} task(s) to archive
                </span>
                <ul className="mt-2 space-y-1">
                  {diff.tasks_to_archive.map((t, i) => (
                    <li key={i} className="text-sm text-slate-300">{t.title} <span className="text-xs text-slate-300">· {t.reason}</span></li>
                  ))}
                </ul>
              </label>
            )}

            {(diff.date_changes || []).length > 0 && (
              <label className="block rounded-xl border border-blue-500/25 bg-blue-500/5 p-4">
                <span className="text-sm font-semibold text-blue-300 flex items-center gap-2">
                  <input type="checkbox" checked={apply.dates} onChange={(e) => setApply({ ...apply, dates: e.target.checked })}
                    data-testid="apply-dates" />
                  <CalendarClock className="w-4 h-4" /> {diff.date_changes.length} date change(s)
                </span>
                <ul className="mt-2 space-y-1">
                  {diff.date_changes.map((t, i) => (
                    <li key={i} className="text-sm text-slate-300">{t.title} → {fmtDate(t.new_due_date)} <span className="text-xs text-slate-300">· {t.reason}</span></li>
                  ))}
                </ul>
              </label>
            )}

            <p className="text-xs text-emerald-300/80 inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Everything you've already completed is preserved, and this version is saved to your history.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <button onClick={resync} disabled={busy}
                className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold"
                data-testid="apply-resync-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Apply these updates
              </button>
              <button onClick={() => { setDiff(null); setPreviewId(null); }}
                className="text-sm text-slate-300 hover:text-white" data-testid="back-to-changes-btn">Back</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
